/* eslint-disable @reatom/unit-naming-rule */
import { action } from '@reatom/framework'
import { match } from 'ts-pattern'

import { pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { TxSerialized } from '@root/entities/transaction'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error } from '@root/shared'
import { ApiError, apiRequest } from '@root/shared/api'

import type { TypeHelper } from '@blade/std-lib'
import type {
  ReferralClaimTransactionCreateData,
  ReferralClaimTransactionCreatePayload,
  ReferralSendClaimTransactionCreatePayload,
} from '@blade/trade-api-client'
import type { ReferralCode } from '@root/entities/referral'
import type { TxSigned } from '@root/entities/transaction'
import type { WalletId, WalletModel } from '@root/entities/wallet'
import type BigNumber from 'bignumber.js'

type PrepareClaimTransactionRequest = TypeHelper.OverrideKeys<
  ReferralClaimTransactionCreatePayload,
  { network: Network.Network; walletId: WalletId }
>
export const referralPrepareClaimTransactionRequestAction = action(
  async (ctx, req: PrepareClaimTransactionRequest) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.referral.referralClaimTransactionCreate, [
        {
          ...req,
          network: Network.toDto(req.network),
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'withdrawReferralRequestAction',
)

export const referralSendClaimTransactionRequestAction = action(
  async (
    ctx,
    req: TypeHelper.OverrideKeys<
      ReferralSendClaimTransactionCreatePayload,
      { network: Network.Network; signedTx: TxSigned }
    >,
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.referral.referralSendClaimTransactionCreate,
        [
          {
            ...req,
            network: Network.toDto(req.network),
          },
        ],
      ),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'withdrawReferralRequestAction',
)

type ClaimRequest = {
  wallet: WalletModel
  network: Network.Network
  amount: BigNumber
  referralCode: ReferralCode
}
export const processClaimReferralTransactionAction = action(
  async (ctx, { amount: _, network, referralCode, wallet }: ClaimRequest) => {
    // TODO: Check amount
    const { secureStorageService } = ctx.get(dependenciesAtom)

    const prepareResult = await ctx.schedule(() =>
      referralPrepareClaimTransactionRequestAction(ctx, {
        network: network,
        walletId: wallet.id,
        referralCode: referralCode,
      }),
    )

    if (Result.isFailure(prepareResult)) {
      return prepareResult
    }

    const processSvm = async (
      prepareData: ReferralClaimTransactionCreateData,
    ) => {
      if (network !== Network.Solana) {
        return Result.fail(Error.UnknownError.fromUnknown('Network mismatch'))
      }
      if (!('transaction' in prepareData)) {
        return Result.fail(
          Error.UnknownError.fromUnknown('Invalid response for SVM'),
        )
      }

      const signedTxResult = await ctx.schedule(() =>
        secureStorageService.signPlainTransaction({
          network: network,
          walletId: wallet.id,
          tx: TxSerialized(prepareData.transaction),
        }),
      )

      return signedTxResult
    }

    const processEvm = async (
      prepareData: ReferralClaimTransactionCreateData,
    ) => {
      if (!Network.isEvmNetwork(network)) {
        return Result.fail(Error.UnknownError.fromUnknown('Network mismatch'))
      }
      if (!('data' in prepareData)) {
        return Result.fail(
          Error.UnknownError.fromUnknown('Invalid response for SVM'),
        )
      }

      const signedTxResult = await ctx.schedule(() =>
        secureStorageService.signPlainTransaction({
          network: network,
          walletId: wallet.id,
          tx: TxSerialized(prepareData.transaction),
        }),
      )

      return signedTxResult
    }

    const signedResult = Result.unify(
      await ctx.schedule(() =>
        match(network)
          .with(Network.Solana, () => processSvm(prepareResult.success))
          .with(Network.Base, () => processEvm(prepareResult.success))
          .with(Network.Ethereum, () => processEvm(prepareResult.success))
          .with(Network.Bsc, () => processEvm(prepareResult.success))
          .with(Network.Ton, () =>
            Result.fail(Error.UnknownError.fromUnknown('Unsupported network')),
          )
          .exhaustive(),
      ),
    )

    if (Result.isFailure(signedResult)) {
      return signedResult
    }

    const sendResult = await ctx.schedule(() =>
      referralSendClaimTransactionRequestAction(ctx, {
        network: network,
        signedTx: signedResult.success.txSigned,
      }),
    )

    return sendResult
  },
)
